import { useState } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SupplierTypeahead } from './SupplierTypeahead';
import { PortalRequestModal } from './PortalRequestModal';

interface TestConnectionFormProps {
  onSubmit: (data: {
    supplierId: string;
    supplierName: string;
    username: string;
    password: string;
  }) => void;
  isLoading: boolean;
  remainingAttempts?: number;
  isRateLimited?: boolean;
  rateLimitMessage?: string;
}

export function TestConnectionForm({ onSubmit, isLoading, remainingAttempts, isRateLimited, rateLimitMessage }: TestConnectionFormProps) {
  const [supplierId, setSupplierId] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPortalRequest, setShowPortalRequest] = useState(false);

  const isValid = supplierId && username.length >= 3 && password.length >= 3 && !isRateLimited;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid && !isLoading) {
      onSubmit({ supplierId, supplierName, username, password });
    }
  };

  return (
    <Card className="shadow-lg border-0 bg-gradient-to-br from-white to-blue-50/30">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-xl">
          <span className="text-2xl">🔐</span>
          Test Portal Credentials
        </CardTitle>
        <CardDescription className="text-base">
          Enter supplier portal credentials to test connection
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Section 1: Select Portal */}
          <div className="space-y-4 p-4 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 rounded-lg border border-blue-100">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-sm">1</div>
              <h3 className="text-lg font-semibold text-blue-900">Select Portal</h3>
            </div>
            <p className="text-sm text-blue-700 ml-11 font-medium">Please search the portal for which you would like to test</p>
            <div className="ml-8 space-y-2">
              <SupplierTypeahead
                value={supplierId}
                selectedLabel={supplierName}
                onSelect={(id, name) => {
                  setSupplierId(id);
                  setSupplierName(name);
                }}
                disabled={isLoading}
              />
              <p className="text-xs text-blue-600">
                <button 
                  type="button"
                  onClick={() => setShowPortalRequest(true)}
                  className="hover:underline cursor-pointer"
                >
                  Can't find your portal? Send request here
                </button>
              </p>
            </div>
          </div>

          {/* Section 2: Portal Credentials */}
          <div className="space-y-4 p-4 bg-gradient-to-r from-green-50/50 to-emerald-50/50 rounded-lg border border-green-100">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-sm">2</div>
              <h3 className="text-lg font-semibold text-green-900">Portal Credentials</h3>
            </div>
            <div className="ml-8 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">User ID</Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter User ID"
                  disabled={isLoading}
                  minLength={3}
                />
                <p className="text-xs text-muted-foreground">User ID can be either username or email for login to the website</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    disabled={isLoading}
                    minLength={3}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Status Messages */}
          {remainingAttempts !== undefined && remainingAttempts < 3 && (
            <p className="text-sm text-warning">
              {remainingAttempts} attempt{remainingAttempts !== 1 ? 's' : ''} remaining
            </p>
          )}

          {isRateLimited && rateLimitMessage && (
            <p className="text-sm text-destructive bg-destructive/10 p-2 rounded">
              {rateLimitMessage}
            </p>
          )}

          {/* Submit Button */}
              <Button
                type="submit"
                className="w-full h-12 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02]"
                disabled={!isValid || isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    <span className="text-lg">Testing Connection...</span>
                  </>
                ) : (
                  <>
                    <span className="mr-2 text-xl">🚀</span>
                    <span className="text-lg">Test Portal Bot</span>
                  </>
                )}
              </Button>
              
              <p className="text-xs text-center text-slate-500 mt-3">
                Credentials are not stored • Rate limit: 5 attempts per 5 minutes
              </p>
        </form>
      </CardContent>
      
      <PortalRequestModal 
        open={showPortalRequest} 
        onOpenChange={setShowPortalRequest} 
      />
    </Card>
  );
}
